import React, { useMemo, useState } from "react";
import { Send, MessageCircle, Building2, CheckCircle2, FileDown } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Chat com abas separadas:
 *  - "Família" (thread_type = family_consultor)
 *  - "Clínica" (thread_type = consultor_clinic) — só consultor/admin
 *
 * A família nunca vê a aba Clínica. O componente é reutilizado — quando
 * hasClinicTab=false, mostra só a aba família (visão da família).
 *
 * `familySelection`: se presente, renderiza card fixado no topo da aba família
 *   com o plano escolhido — contexto sempre visível para consultor + família.
 */
export const CaseChat = ({
  messages, draft, setDraft, onSend,       // canal família (default)
  clinicDraft = "", setClinicDraft = () => {},
  onSendToClinic = () => {},
  hasClinicTab = true,
  clinicCode = null,
  clinicReserved = false,
  familySelection = null,
  caseId = null,
}) => {
  const [tab, setTab] = useState("familia");

  // Split messages into threads. Msg sem thread_type → assumir família.
  const { familyMsgs, clinicMsgs } = useMemo(() => {
    const fam = [], cli = [];
    for (const m of messages) {
      const t = m.thread_type || "family_consultor";
      if (t === "consultor_clinic" || m.sender_role === "clinica") cli.push(m);
      else fam.push(m);
    }
    return { familyMsgs: fam, clinicMsgs: cli };
  }, [messages]);

  const activeMsgs = tab === "familia" ? familyMsgs : clinicMsgs;
  const activeDraft = tab === "familia" ? draft : clinicDraft;
  const setActiveDraft = tab === "familia" ? setDraft : setClinicDraft;
  const doSend = tab === "familia" ? onSend : onSendToClinic;
  const placeholder = tab === "familia" ? "Mensagem para a família…" : "Mensagem para a Unidade Parceira…";

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4 sticky top-24" data-testid="case-chat">
      <div className="flex items-center gap-1 border-b border-slate-100 mb-3">
        <button
          onClick={() => setTab("familia")}
          data-testid="chat-tab-familia"
          className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border-b-2 transition-colors ${tab === "familia" ? "border-[#0B2B62] text-[#0B2B62]" : "border-transparent text-slate-500 hover:text-slate-700"}`}
        >
          <MessageCircle className="w-4 h-4" />
          Família
          {familyMsgs.length > 0 && <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 rounded-full">{familyMsgs.length}</span>}
        </button>
        {hasClinicTab && (
          <button
            onClick={() => setTab("clinica")}
            data-testid="chat-tab-clinica"
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border-b-2 transition-colors ${tab === "clinica" ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          >
            <Building2 className="w-4 h-4" />
            Clínica{clinicCode ? ` ${clinicCode}` : ""}
            {clinicMsgs.length > 0 && <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 rounded-full">{clinicMsgs.length}</span>}
          </button>
        )}
      </div>

      {tab === "clinica" && !clinicReserved && (
        <div className="mb-2 px-2 py-1.5 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
          Ainda não há reserva confirmada — a clínica só recebe suas mensagens após a reserva.
        </div>
      )}

      {tab === "familia" && familySelection && (
        <div className="mb-3 p-3 rounded-lg border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-blue-50" data-testid="chat-plan-pin">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Plano escolhido · fixado
              </div>
              <div className="mt-1 font-display font-bold text-sm text-[#0B2B62] leading-tight">
                {familySelection.plan_tier}
              </div>
              <div className="text-xs text-slate-700 mt-0.5">
                Clínica <b>{familySelection.clinic_code}</b>
                {familySelection.clinic_city && <> · {familySelection.clinic_city}/{familySelection.clinic_state}</>}
              </div>
              {familySelection.plan_monthly_price != null && (
                <div className="text-xs font-mono text-emerald-700 font-bold mt-0.5">
                  R$ {Number(familySelection.plan_monthly_price).toLocaleString("pt-BR",{minimumFractionDigits:2})}/mês
                </div>
              )}
              {familySelection.notes && (
                <div className="text-[11px] text-slate-600 mt-1 italic">"{familySelection.notes}"</div>
              )}
            </div>
            {caseId && (
              <a href={`${API_BASE}/api/cases/${caseId}/plan-receipt.pdf`}
                 target="_blank" rel="noopener noreferrer"
                 data-testid="chat-plan-pin-receipt"
                 className="shrink-0 inline-flex items-center gap-1 px-2 py-1 bg-[#0B2B62] text-white rounded-md text-[10px] font-semibold hover:bg-slate-800">
                <FileDown className="w-3 h-3" /> PDF
              </a>
            )}
          </div>
        </div>
      )}

      <div className="h-72 overflow-y-auto space-y-2 pr-1 mb-3" data-testid={`consultant-chat-${tab}`}>
        {activeMsgs.length === 0 && (
          <p className="text-sm text-slate-500">
            {tab === "familia" ? "Nenhuma mensagem com a família ainda." : "Nenhuma mensagem com a clínica ainda."}
          </p>
        )}
        {activeMsgs.map((m) => {
          const mine = m.sender_role === "consultor" || m.sender_role === "admin";
          const bubbleColor = tab === "familia"
            ? (mine ? "bg-[#0B2B62] text-white" : "bg-slate-100 text-slate-800")
            : (mine ? "bg-emerald-700 text-white" : "bg-emerald-50 text-emerald-900 border border-emerald-200");
          return (
            <div key={m.id} className={`max-w-[85%] ${mine ? "ml-auto" : ""}`}>
              <div className={`rounded-lg px-3 py-2 text-sm ${bubbleColor}`}>{m.content}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">{m.sender_name}</div>
            </div>
          );
        })}
      </div>

      <div className="flex gap-2">
        <input
          data-testid={`chat-input-${tab}`}
          value={activeDraft}
          onChange={(e) => setActiveDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && doSend()}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#0B2B62]"
        />
        <button
          data-testid={`chat-send-${tab}`}
          onClick={doSend}
          className={`p-2 text-white rounded-md ${tab === "clinica" ? "bg-emerald-700 hover:bg-emerald-800" : "bg-[#0B2B62] hover:bg-slate-800"}`}
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
};
